import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';
import { colors, sidebar } from '../design/tokens';

interface EmailUsageData {
  monthly_limit: number;
  current_usage: number;
  usage_percentage: number;
}

interface Props {
  /** Rendered in the sidebar footer, which is dark. The page greens and
      ambers are tuned for white and go muddy there, so the dark variant
      swaps both the text and the empty track. */
  onDark?: boolean;
}

const EmailUsageIndicator: React.FC<Props> = ({ onDark = false }) => {
  const { token } = useAuth();
  const [usage, setUsage] = useState<EmailUsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    try {
      const response = await axios.get<EmailUsageData>(
        `${config.apiBaseUrl}/api/v1/admin/email-limits/current-usage`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch email usage:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUsage();
    }
  }, [token, fetchUsage]);

  if (!token || loading || !usage) {
    return null;
  }


  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.875rem', color: onDark ? sidebar.textMuted : colors.textSecondary }}>Email Usage:</span>
      <div style={{ position: 'relative', width: '120px' }}>
        <div style={{ 
          width: '100%', 
          backgroundColor: onDark ? 'rgba(255, 255, 255, 0.16)' : '#e5e7eb', 
          borderRadius: '9999px', 
          height: '0.5rem' 
        }}>
          <div
            style={{ 
              height: '0.5rem', 
              borderRadius: '9999px',
              transition: 'all 0.3s',
              backgroundColor: usage.usage_percentage >= 90 ? '#dc2626' : 
                             usage.usage_percentage >= 75 ? '#f59e0b' : '#10b981',
              width: `${Math.min(usage.usage_percentage, 100)}%`
            }}
          />
        </div>
      </div>
      <span style={{ 
        fontSize: '0.75rem', 
        // The bar may stay bright; the number beside it is read, so it takes
        // the tokens, which clear 4.5:1 on white.
        color: onDark
          ? (usage.usage_percentage >= 90 ? '#FCA5A5' :
             usage.usage_percentage >= 75 ? '#FCD34D' : '#6EE7B7')
          : (usage.usage_percentage >= 90 ? colors.danger : 
             usage.usage_percentage >= 75 ? colors.warning : colors.success),
        fontWeight: '500'
      }}>
        {usage.current_usage}/{usage.monthly_limit}
      </span>
    </div>
  );
};

export default EmailUsageIndicator;